import * as Sentry from '@sentry/nuxt'
import pickBy from 'lodash/pickBy'
import urljoin from 'url-join'

import pkg from '../../package.json'

import { useRuntimeConfig } from '#app'
import { defineNuxtPlugin } from '#imports'
import { useAuthToken } from '~/composables/auth/useAuthToken.js'
import { getErr998SentryExtra, checkErr998SelfDedupe, getUrlEndpointPath } from '~/utils/err998SentryExtra.js'
import { claimErr998ReportSlot } from '~/utils/suppressErr998Report.js'

export default defineNuxtPlugin((nuxtApp) => {
  const { apiUrl, clientId, intranet } = useRuntimeConfig().public
  const { getAuthHeaders } = useAuthToken()

  let _errorHandler = null
  function setErrorHandler(handler) {
    _errorHandler = handler
  }

  // 清除 error handler，避免閉包持有舊的元件引用
  function clearErrorHandler() {
    _errorHandler = null
  }

  function isValid(value) {
    return value !== undefined && value !== null
  }

  // 送出前 token 閘門已於 request() 內恢復（bb76daa7e 曾移除、全交後端回 ERR_A103，導致無 token 請求
  // 照送並在後端產生大量 TOKEN_CHECK_FAIL）。現以後端回應形狀合成 ERR_A103 並餵入 _errorHandler，
  // 保留 TI-15375 的屏蔽/GA/靜默回大廳管線；後端 ERR_A103 仍為兜底（isPublic 漏標、SSR 等情境）。
  const noRetryErrorCodes = ['ERR_F108', 'ERR_G111', 'ERR_A224', 'ERR_P132']

  // 請求攔截器列表
  const requestInterceptors = []

  // 註冊攔截器
  const addRequestInterceptor = (interceptor) => {
    requestInterceptors.push(interceptor)
    // 返回移除函數
    return () => {
      const index = requestInterceptors.indexOf(interceptor)
      if (index > -1) {
        requestInterceptors.splice(index, 1)
      }
    }
  }

  // 清除所有攔截器，避免閉包累積造成記憶體洩漏
  const clearAllInterceptors = () => {
    requestInterceptors.length = 0
  }

  // 取得當前攔截器數量（用於除錯）
  const getInterceptorCount = () => requestInterceptors.length

  const request = async (options) => {
    let endpoint
    try {
      // 執行攔截器 - 允許修改 options 或進行其他處理
      for (const interceptor of requestInterceptors) {
        // 如果攔截器返回 false，則取消請求
        const result = interceptor(options)
        if (result === false) {
          const cancelError = new Error('Canceled')
          cancelError.name = 'CanceledError'
          cancelError.__isCancel = true
          throw cancelError
        }
      }

      const { url, method, params, isPublic, headers, isFormData = false, signal } = options
      const baseUrl =
        import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : nuxtApp.$getFastApiUrl?.() || apiUrl
      endpoint = urljoin(baseUrl, url.replace(':clientId', clientId))

      // 原子化快照：閘門判斷與實際送出使用同一次 header 讀取，
      // 避免 cookie ref 在守衛檢查與 $fetch 之間被 CookieStore 清空（TOCTOU）
      const authHeaders = getAuthHeaders()
      if (!isPublic && !authHeaders.Authorization) {
        const blockedError = {
          code: 'ERR_A103',
          name: 'TOKEN_NEEDED',
          message: 'No token provided',
          http_url: endpoint,
          http_method: method.toUpperCase(),
        }
        _errorHandler && _errorHandler(blockedError)
        const gateError = new Error(blockedError.message)
        Object.assign(gateError, blockedError)
        gateError.url = endpoint
        throw gateError
      }

      const isGet = method.toLowerCase() === 'get'
      const payloadKey = isGet ? 'query' : 'body'
      const payload = isFormData ? params : pickBy(params, isValid)

      const res = await $fetch(endpoint, {
        method,
        headers: {
          'X-TH1GAMES-VERSION': `v${pkg.version}`,
          'X-TH1GAMES-PLATFORM': 'pwa',
          ...authHeaders,
          ...headers,
        },
        [payloadKey]: payload,
        retry: 2,
        retryDelay: 1000, // 重試延遲 1 秒（ofetch 會自動使用指數退避）
        retryStatusCodes: [408, 409, 425, 429, 500, 502, 504], // 需要重試的 HTTP 狀態碼
        signal, // 傳遞 signal 支援 AbortController
        onResponseError({ response }) {
          const code = response?._data?.error?.code
          // Explicitly stop ofetch retry when backend business error is in no-retry list.
          if (code && noRetryErrorCodes.includes(code)) {
            const noRetryError = new Error(response?._data?.error?.message || `No-retry error: ${code}`)
            noRetryError.data = response?._data
            // 保留 response 供 catch block 的 e.response?.status 取值，否則 no-retry 錯誤碼
            // （ERR_F108/ERR_G111/ERR_A224/ERR_P132）的 error_event 會漏帶真實 http_status，
            // 退回 fallback 值 0（發現於 JT-1777948860 動態驗證：登入失敗 ERR_A224 的 http_status 恆為 0）。
            noRetryError.response = response
            throw noRetryError
          }
        },
      })

      if (res.data?.error) {
        const apiError = new Error(res.data.error.message || res.data.error.code || 'API Error')
        Object.assign(apiError, res.data.error)
        // 標記為後端業務錯誤（2xx body 帶 error payload，非 JS 回歸）：供 fire-and-forget 呼叫端辨識，
        // 避免已由 store fail 狀態呈現的業務錯誤再變成未捕捉 rejection 重複上報（CLIENT-PWA-SSR-16S）。
        // 本分支由 `if (res.data?.error)` 守門，error payload 必存在。
        apiError.isBusinessError = true
        throw apiError
      }
      return res
    } catch (e) {
      // 忽略各種形式的請求取消錯誤
      const isAbortError =
        e.__isCancel ||
        e.name === 'AbortError' ||
        e.name === 'CanceledError' ||
        e.message?.toLowerCase().includes('abort') ||
        e.message?.toLowerCase().includes('cancel')

      if (isAbortError) {
        console.log('[Request] Canceled or Aborted')
        return { data: {} }
      }

      if (e.message?.includes('Failed to fetch')) {
        // 只有在 UserInfo 已經被初始化的情況下才抓取 uidSite，避免插件初始化順序衝突
        const uidSite = nuxtApp._userInfoContext?.uidSite?.value
        const errData998 = getErr998SentryExtra({
          endpoint,
          method: options?.method,
          clientId,
          uidSite,
          clientIp: nuxtApp.$clientIp?.value,
        })
        // 離線時的網路錯誤屬預期行為（裝置連線中斷），不上報 Sentry 以降低雜訊，與 useAuth.js 現有 pattern 一致；
        // SSR 沒有 navigator，typeof 檢查為 false，維持原行為正常上報
        const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
        if (!isOffline) {
          // 以 host+endpoint 為節流鍵：斷線期間背景輪詢會讓同一 host+endpoint 每個 tick 重複失敗，
          // 時間窗內只留第一筆，避免單一使用者灌爆同一個 issue（CLIENT-PWA-SSR-1GF）
          const fingerprint = ['ERR_998', errData998.method, errData998.apiHost, errData998.endpoint]
          // FE-1：/self 時間窗去重 —— 同一站台 + API 線 + self + 使用者，10 分鐘內只送 Sentry 一次，
          // 其餘失敗不上報，僅累計 suppressedCount（附在下次真正送出的那一筆）。注意：去重只影響是否
          // captureException，不可提早 return —— 下方仍需維持原行為 throw e，讓呼叫端的業務錯誤處理不受影響。
          let suppressedCount = 0
          let shouldReportErr998 = true
          if (errData998.endpointClass === 'self') {
            // uidSite 為空（尚未登入/尚未拿到會員 id）時改用共用鍵去重，避免完全跳過去重
            // 導致匿名 /self 失敗照樣每次上報；待 FE-10 補上匿名 session 鍵後可再依分頁細分
            const dedupeUser = errData998.uidSite || 'anonymous'
            const dedupeKey = [errData998.site, errData998.apiHost, 'self', dedupeUser].join('|')
            const dedupe = checkErr998SelfDedupe(dedupeKey)
            shouldReportErr998 = dedupe.allow
            suppressedCount = dedupe.suppressedCount
          } else if (errData998.endpointClass !== 'login') {
            // self 以外、且非 login 的端點沿用原本 60 秒 host+endpoint 節流；
            // login 端點刻意不節流，確保每次登入失敗都能上報，不被降噪機制吃掉
            const throttle = claimErr998ReportSlot(fingerprint.join('|'))
            shouldReportErr998 = throttle.shouldReport
            suppressedCount = throttle.suppressedCount
          }

          if (shouldReportErr998) {
            // 改用去敏結構化欄位組標題並指定 fingerprint：避免含 appSecret 的完整 URL 進 Sentry 標題，
            // 並以 host+endpoint 分組，將「每 URL 一個 issue」收斂為「每 host+endpoint 一個」（issue #404 Option B）
            const sentryError = new Error(`ERR_998: [${errData998.method}] ${errData998.apiHost}${errData998.endpoint} <no response>`)
            sentryError.name = 'ERR_998'
            const redactedErrorMessage = e.message?.replace(/\?[^\s"]*/g, '?[REDACTED]')
            // SSR（Node fetch/undici）失敗時 error.cause.code 可能帶 ENOTFOUND/ECONNREFUSED 等細節；
            // 瀏覽器端 fetch() 規範上不提供此細節，此時為 undefined，故僅在有值時才附加
            const networkErrorCode = e.cause?.code
            Sentry.captureException(sentryError, {
              level: 'warning',
              fingerprint,
              tags: { ...errData998, ...(networkErrorCode ? { networkErrorCode } : {}) },
              extra: {
                ...errData998,
                errorMessage: redactedErrorMessage,
                networkErrorCode: networkErrorCode || null,
                hostTestResults: nuxtApp._hostTesterContext?.hostTester?.lastResults || [],
                ...(suppressedCount > 0 ? { suppressedCount } : {}),
              },
            })
          }
        }
      } else if (e.data) {
        // ERR_A108 token 過期：交付錯誤處理器（屏蔽/登出）前先靜默重試一次，
        // 涵蓋 extendToken 輪替 token 的短暫競態，降低真正屏蔽的次數。
        if (e.data.error?.code === 'ERR_A108' && !options._a108Retried) {
          return await request({ ...options, _a108Retried: true })
        }
        // 標記 isBusinessError 供 fire-and-forget 呼叫端安全吞掉：此類已交付 _errorHandler 呈現（CLIENT-PWA-SSR-16S）
        if (e.data.error) {
          e.data.error.http_url = endpoint
          e.data.error.http_method = options.method?.toUpperCase()
          e.data.error.http_status = e.statusCode ?? e.status ?? e.response?.status ?? null
          _errorHandler && _errorHandler(e.data.error)
          const dataError = new Error(e.data.error.message || e.data.error.code || 'Request Error')
          Object.assign(dataError, e.data.error)
          dataError.isBusinessError = true
          throw dataError
        }
        // 契約異常回應刻意不標記 isBusinessError 且照常 throw，以利上報、不遮蔽問題（CLIENT-PWA-SSR-16S）；
        // 端點去 query 避免 appSecret 進 Sentry 標題，去版本號與 clientId 避免同一支 API 依站台拆成多個 issue
        const responseStatus = e.statusCode ?? e.status ?? e.response?.status ?? null
        const malformedError = new Error(`[${options?.method ?? ''}] ${getUrlEndpointPath(endpoint)} <${responseStatus ?? 'no status'}>`)
        malformedError.name = 'MalformedApiResponse'
        malformedError.status = responseStatus
        throw malformedError
      }
      throw e
    }
  }

  // SSR 請求結束時清理 interceptors 和 error handler
  // 避免閉包累積造成記憶體洩漏
  if (import.meta.server) {
    nuxtApp.hooks.hook('app:rendered', () => {
      clearAllInterceptors()
      clearErrorHandler()
    })
  }

  return {
    provide: {
      request: {
        send: request,
        setErrorHandler,
        addRequestInterceptor,
        clearErrorHandler,
        clearAllInterceptors,
        getInterceptorCount,
      },
    },
  }
})
